import { useCollection } from '@amzn/awsui-collection-hooks';
import { CollectionPreferencesProps, Pagination, Table } from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@/src/store/user.context';
import { ChallengeDescriptor } from '@/src/types/Challenge';
import { useChallenges } from '@/src/store/challenge.context';
import { EventPermission } from '@/src/types/Event';
import { Nullable } from '../../../types/common';
import { ApprovableDiff } from '../../../types/ResourceDeployment';
import { i18nKeys } from '../../../utils/i18n.utils';
import { paginationLabels } from '../../../utils/table.utils';
import { TableHeader } from '../TableHeader';
import RevisionDetails from './RevisionDetails';
import { COLUMN_DEFINITIONS, NORMAL_USER_COLUMN_DEFINITIONS } from './revisions-list-config';
import './Revisions.scss';

export interface ChangeWithRevisionNumber extends ApprovableDiff {
  revisionNumber: number;
}

interface RevisionsProps {
  changes: any[];
  challengeDescriptors: ChallengeDescriptor[];
  eventPermissions: EventPermission[];
}

export const Revisions: React.FC<RevisionsProps> = ({ changes, challengeDescriptors, eventPermissions }) => {
  const { t } = useTranslation();
  const [changeHistory, setChangeHistory] = useState<ChangeWithRevisionNumber[]>();
  const [selectedRevision, setSelectedRevision] = useState<Nullable<ChangeWithRevisionNumber>>(null);
  const { user } = useUser();
  const [revisionTypeExpanded, setRevisionTypeExpanded] = useState<number[]>([]);
  const isNormalUser = !!user?.hasBasicAccess;

  useEffect(() => {
    let newChangesList = changes;
    newChangesList = newChangesList.filter((change) => !!change?.changeAppliedDate);
    newChangesList = newChangesList.sort((a, b) => {
      if (b.changeAppliedDate && a.changeAppliedDate) {
        return b.changeAppliedDate - a.changeAppliedDate;
      } else {
        return -1;
      }
    });
    newChangesList.forEach((change, index) => {
      change.revisionNumber = newChangesList.length - index;
      return change as ChangeWithRevisionNumber;
    });
    setChangeHistory(newChangesList);

    changes = changes.filter((change) => !!change.changeAppliedDate);
  }, [changes]);

  const [preferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
    visibleContent: ['changeAppliedDate'],
  });

  const { items, collectionProps, paginationProps } = useCollection(changeHistory || [], {
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
  });

  const toggleRevisionTypeExpanded = (revisionNumber: number) => {
    setRevisionTypeExpanded((prev) => {
      if (prev.includes(revisionNumber)) {
        return prev.filter((prevRevisionNumber) => prevRevisionNumber !== revisionNumber);
      }
      return [...prev, revisionNumber];
    });
  };

  const columnDefinitions = isNormalUser
    ? NORMAL_USER_COLUMN_DEFINITIONS({
        revisionTypeExpanded,
        challengeDescriptors,
        toggleRevisionTypeExpanded,
        eventPermissions,
      })
    : COLUMN_DEFINITIONS(setSelectedRevision);

  // TODO: Once available, implement comparison logic
  // TODO: Create UI friendly dictionary to replace property labels
  return (
    <div className={`mt-10 ${isNormalUser ? 'revision-table' : ''}`} style={{ paddingBottom: '16px' }}>
      {!selectedRevision ? (
        <React.Fragment>
          <Table
            {...collectionProps}
            resizableColumns
            header={<TableHeader totalItems={changes.length} title={t(i18nKeys.revisions.publishedRevisions.title)} />}
            columnDefinitions={columnDefinitions}
            items={items}
            pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels(t)} />}
          />
        </React.Fragment>
      ) : (
        <RevisionDetails revision={selectedRevision} goBack={() => setSelectedRevision(null)} />
      )}
    </div>
  );
};

export default Revisions;
